import classNames from 'classnames';
import { motion, AnimatePresence } from 'framer-motion';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useEffect, useRef, type PropsWithChildren } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { Button, Container, Grid, Icon, Segment } from 'semantic-ui-react';
import { useLocalStorage } from 'usehooks-ts';

import { getDocsUrl } from '@routes/utils';
import { useAppSelector } from '@store/hooks';
import ContextPanel from '@components/context-panel';
import Err from '@components/err';
import { usePermissions } from '@components/permissions';
import { useRouteContext } from '@components/router';
import AppTour from '@features/app-tour';
import useIsAuthenticated from '@hooks/use-is-authenticated';

import { useAppContext } from './app-context';
import { CustomBanners } from './custom-banner';
import { GlobalContextPanel } from './global-context-panel';
import HealthErr from './health-err';
import LayoutWrapper from './layout-wrapper';
import { PageOffsetAnchor } from './offset-anchor';
import SideNav from './side-nav';
import TopNav from './topnav';
import s from './layout.scss';

const SIDENAV_OPEN_WIDTH = '20rem';
const SIDENAV_CLOSED_WIDTH = '4.05rem';

// What to display if JS is disabled in the browser
// TODO: Consider whether this could/should be elsewhere
const NoScript = ({ children }: PropsWithChildren) => {
  const staticMarkup = renderToStaticMarkup(children);
  return <noscript dangerouslySetInnerHTML={{ __html: staticMarkup }} />; // eslint-disable-line react/no-danger
};

/**
 * Main layout component for the application. Provides the high-level structure
 * to frame all views.
 */
const Layout = ({ children }: PropsWithChildren) => {
  useStyles(s);

  const {
    anchoreInternalDeployment,
    customLinks,
    enrichInventoryView,
    whatsNew,
    uiCommitSha,
    uiImageBuildTimestamp,
    uiVersion,
  } = useAppContext();

  const { routeMask = '/', route, hash } = useRouteContext();

  const { permissions } = usePermissions();

  // Side navigation open state is persisted to local storage
  const [isSideNavOpen, setIsSideNavOpen] = useLocalStorage('sidenav', true);

  // Pointers to the current state of the application
  const isAuthenticated = useIsAuthenticated();
  const account = useAppSelector(state => state.auth.account);

  const { isLicenseGood } = useAppSelector(state => state.app.healthCheck);

  // `wasAuthenticatedRef` lags `isAuthenticated` by one commit because the
  // effect updates it after render, letting us detect the false → true
  // transition and skip the sidenav's mount-in width animation.
  const wasAuthenticatedRef = useRef(isAuthenticated);
  useEffect(() => {
    wasAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);
  const clientFreshlyLoggedIn = isAuthenticated && !wasAuthenticatedRef.current;

  if (!isLicenseGood) {
    return (
      <LayoutWrapper withDoodleBackground error>
        <div
          style={{
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Container>
            <Err
              header="No valid license detected&hellip;"
              image="robot_notfound.svg"
              imageSize="small"
              headerColor="orange"
              base
              message={
                <>
                  <p>
                    Anchore Enterprise Client requires a valid license to
                    operate.
                  </p>
                  <br />
                  <p>
                    For instructions on how to activate your license please
                    refer to the&nbsp;
                    <a
                      rel="noopener noreferrer"
                      target="_blank"
                      href={getDocsUrl()}
                    >
                      documentation
                    </a>
                    .
                  </p>
                  <p>
                    You can also&nbsp;
                    <a
                      rel="noopener noreferrer"
                      target="_blank"
                      href="https://get.anchore.com/contact/"
                    >
                      request a full or trial license
                    </a>
                    &nbsp;or contact&nbsp;
                    <a
                      rel="noopener noreferrer"
                      target="_blank"
                      href="https://support.anchore.com/"
                    >
                      Anchore Support
                    </a>
                    .
                  </p>
                </>
              }
            />
          </Container>
        </div>
      </LayoutWrapper>
    );
  }

  if (!isLicenseGood.isBeforeExpiration) {
    return (
      <LayoutWrapper withDoodleBackground error>
        <div
          style={{
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Container>
            <Err
              header={
                <span>
                  Your Anchore Enterprise
                  {isLicenseGood.type === 'trial' ? ' trial ' : ' '}
                  license has expired&hellip;
                </span>
              }
              image="robot_flame.svg"
              imageSize="medium"
              headerColor="red"
              base
              message={
                <>
                  <p>
                    For instructions on how to renew your license please refer
                    to the&nbsp;
                    <a
                      rel="noopener noreferrer"
                      target="_blank"
                      href={getDocsUrl()}
                    >
                      documentation
                    </a>
                    .
                  </p>
                  <p>
                    You can also&nbsp;
                    <a
                      rel="noopener noreferrer"
                      target="_blank"
                      href="https://get.anchore.com/contact/"
                    >
                      request full or trial license
                    </a>
                    &nbsp;or contact&nbsp;
                    <a
                      rel="noopener noreferrer"
                      target="_blank"
                      href="https://support.anchore.com/"
                    >
                      Anchore Support
                    </a>
                    .
                  </p>
                </>
              }
            />
          </Container>
        </div>
      </LayoutWrapper>
    );
  }

  // Framer motion animations for the left and right panels
  const framerProps = {
    leftPanel: isAuthenticated
      ? {
          initial: {
            width: isSideNavOpen ? SIDENAV_OPEN_WIDTH : SIDENAV_CLOSED_WIDTH,
          },
          animate: {
            width: isSideNavOpen ? SIDENAV_OPEN_WIDTH : SIDENAV_CLOSED_WIDTH,
          },
          transition: {
            duration: clientFreshlyLoggedIn ? 0 : 0.5,
            type: 'spring',
            bounce: 0,
          },
        }
      : {
          initial: { width: 0 },
          animate: { width: 0 },
          transition: { duration: 0 },
        },
    rightPanel: isAuthenticated
      ? {
          initial: {
            marginLeft: isSideNavOpen
              ? SIDENAV_OPEN_WIDTH
              : SIDENAV_CLOSED_WIDTH,
          },
          animate: {
            marginLeft: isSideNavOpen
              ? SIDENAV_OPEN_WIDTH
              : SIDENAV_CLOSED_WIDTH,
          },
          transition: {
            duration: clientFreshlyLoggedIn ? 0 : 0.5,
            type: 'spring',
            bounce: 0,
          },
        }
      : {
          initial: { marginLeft: 0 },
          animate: { marginLeft: 0 },
          transition: { duration: 0 },
        },
  };

  return (
    <LayoutWrapper
      withDoodleBackground={!isAuthenticated}
      isAuthenticated={isAuthenticated}
      isAdmin={account?.isAdmin}
      isSwitched={
        account ? account.context?.accountname !== account.accountname : false
      }
    >
      <ContextPanel.Provider scope="global">
        <div className={s.contentWrapper}>
          <NoScript>
            <div className={s.noscript}>
              <Container>
                <Grid centered columns={1}>
                  <Grid.Column
                    mobile={15}
                    tablet={12}
                    computer={10}
                    largeScreen={8}
                    widescreen={8}
                  >
                    <Segment.Group className="animate__animated animate__fadeIn">
                      <Segment
                        className="animate__animated animate__fadeIn"
                        style={{ padding: '2rem' }}
                      >
                        <h1>We&#39;re sorry&hellip;</h1>
                        <p>
                          &hellip;but JavaScript is required to use the Anchore
                          Enterprise UI Client application. If you think
                          scripts&nbsp;
                          <em>should</em> be enabled on your browser,&nbsp;
                          <a
                            rel="noopener noreferrer"
                            target="_blank"
                            href="https://www.enable-javascript.com"
                          >
                            this site
                          </a>
                          &nbsp;may help.
                        </p>
                        <p>
                          If you&#39;re still having problems, contact us
                          at&nbsp;
                          <a href="https://support.anchore.com/">
                            Anchore Support
                          </a>
                          &nbsp;and we&#39;ll do our best to assist.
                        </p>
                        <blockquote>
                          <p>
                            <em>
                              I have not failed. I have just found 10,000 ways
                              that won&#39;t work.
                            </em>
                          </p>
                          <sub>—Thomas A. Edison</sub>
                        </blockquote>
                        <div className={s.logoSmall} />
                      </Segment>
                      <span className={s.nuke} />
                    </Segment.Group>
                  </Grid.Column>
                </Grid>
              </Container>
            </div>
          </NoScript>

          <TopNav
            customLinks={customLinks}
            isLicenseGood={isLicenseGood}
            permissions={permissions}
            uiCommitSha={uiCommitSha}
            uiImageBuildTimestamp={uiImageBuildTimestamp}
            uiVersion={uiVersion}
            whatsNew={whatsNew}
          />

          <CustomBanners />
          <PageOffsetAnchor />

          <div className={classNames(s.boundingPanel)}>
            {isAuthenticated ? (
              <AnimatePresence initial={false}>
                <motion.div
                  id="leftPanel"
                  className={classNames(s.leftPanel)}
                  {...framerProps.leftPanel}
                >
                  <Button
                    compact
                    size="tiny"
                    onClick={() => setIsSideNavOpen(prev => !prev)}
                    aria-label="toggle sidebar"
                    style={{
                      zIndex: 2,
                      position: 'relative',
                      margin: 0,
                      borderRadius: 0,
                    }}
                  >
                    <Icon
                      name={
                        isSideNavOpen
                          ? 'angle double left'
                          : 'angle double right'
                      }
                      style={{ margin: 0 }}
                    />
                  </Button>
                  <SideNav
                    activeItem={route.navbar?.name ?? ''}
                    isExpanded={isSideNavOpen}
                    enrichInventoryView={enrichInventoryView}
                    routeMask={routeMask}
                    hash={hash}
                  />
                </motion.div>
              </AnimatePresence>
            ) : null}
            <AnimatePresence initial={false}>
              <motion.div {...framerProps.rightPanel} id="rightPanel">
                {children}
              </motion.div>
            </AnimatePresence>
          </div>
          <HealthErr isInternalDeployment={anchoreInternalDeployment} />
          {isAuthenticated && <AppTour routeMask={routeMask} hash={hash} />}
        </div>
        <GlobalContextPanel />
      </ContextPanel.Provider>
    </LayoutWrapper>
  );
};

export default Layout;
